import { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "./Icon";
import { cx } from "@/lib/cx";
import { useAudioPlayback, type Rate } from "@/hooks/useAudioPlayback";
import {
  WAVEFORM_BARS,
  decodeWaveform,
  deterministicWaveform,
  formatAudioTime,
  sanitizeDurationMs,
  waveformFromBase64,
} from "@/lib/waveform";

/**
 * Player da bolha de voz no arranjo do WhatsApp: play/pause redondo de 34px,
 * onda de barras finas com a bolinha do cursor arrastável, duração embaixo à
 * esquerda (a hora fica à direita, no `.bubble-meta`) e a pílula de velocidade
 * 1× / 1,5× / 2× que só aparece durante a reprodução.
 *
 * - `voice` (PTT): bolha compacta só com player + duração.
 * - `audio` (arquivo): variante com nota musical + nome do arquivo.
 */

interface VoicePlayerProps {
  url: string | null;
  /** Seed do fallback determinístico (ex. `message.id`). */
  seed: string;
  kind: "voice" | "audio";
  fileName?: string | null;
  /** Duração conhecida (otimista local ou resposta do servidor); tolera ausência. */
  durationMs?: number | null;
  /** Waveform que o WhatsApp já mandou (base64, 64 amostras 0..100). */
  waveform?: string | null;
  label: string;
}

function rateLabel(rate: Rate): string {
  return `${String(rate).replace(".", ",")}×`;
}

export function VoicePlayer({ url, seed, kind, fileName, durationMs, waveform, label }: VoicePlayerProps) {
  const [bars, setBars] = useState<number[]>(() => deterministicWaveform(seed));
  const [decodedDuration, setDecodedDuration] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);

  const fallback = useMemo(() => deterministicWaveform(seed), [seed]);

  // A onda do WhatsApp vem pronta no payload: usar ela poupa baixar o arquivo
  // inteiro uma SEGUNDA vez (o `decodeAudioData` refaz o download). O decode
  // fica só para o que não tem onda — as vozes que nós mesmos gravamos.
  const ready = useMemo(() => (waveform ? waveformFromBase64(waveform, WAVEFORM_BARS) : null), [waveform]);

  const knownMs = sanitizeDurationMs(durationMs);
  const {
    audioRef,
    audioProps,
    playing,
    current,
    rate,
    failed,
    listened,
    durationSec,
    progress,
    toggle,
    seekTo,
    toggleRate,
    reset,
  } = useAudioPlayback({
    url,
    seed,
    // `loadedmetadata` tem prioridade e mora no hook; aqui entra so o que
    // sabemos por fora (decode da onda, depois o que o servidor mandou).
    fallbackDurationSec: decodedDuration ?? (knownMs !== undefined ? knownMs / 1000 : null),
  });

  useEffect(() => {
    let alive = true;
    setBars(ready ?? deterministicWaveform(seed));
    setDecodedDuration(null);
    // Um efeito só, como antes: a onda e o tempo zeram no MESMO commit ao
    // trocar de áudio, senão a bolinha fica na posição do áudio anterior.
    reset();
    if (!url || ready) return;
    const controller = new AbortController();
    void decodeWaveform(url, WAVEFORM_BARS, controller.signal).then((result) => {
      if (!alive || !result) return;
      setBars(result.bars);
      setDecodedDuration(result.durationSec);
    });
    return () => {
      alive = false;
      controller.abort();
    };
  }, [url, seed, ready, reset]);

  const playedBars = Math.round(progress * WAVEFORM_BARS);

  const onWaveClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const node = event.currentTarget;
      const rect = node.getBoundingClientRect();
      if (rect.width <= 0) return;
      seekTo((event.clientX - rect.left) / rect.width);
    },
    [seekTo],
  );

  const onWaveKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowRight") {
        event.preventDefault();
        seekTo(progress + 0.05);
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        seekTo(progress - 0.05);
      } else if (event.key === "Home") {
        event.preventDefault();
        seekTo(0);
      } else if (event.key === "End") {
        event.preventDefault();
        seekTo(0.999);
      }
    },
    [progress, seekTo],
  );

  // Arrastar a bolinha: o ponteiro é capturado no `down`, então o cursor pode
  // sair da onda no meio do gesto sem largar a busca.
  const seekFromEvent = useCallback(
    (node: HTMLElement, clientX: number) => {
      const rect = node.getBoundingClientRect();
      if (rect.width > 0) seekTo((clientX - rect.left) / rect.width);
    },
    [seekTo],
  );

  const shownBars = bars.length === WAVEFORM_BARS ? bars : fallback;
  const timeLabel = formatAudioTime(playing || current > 0 ? current : (durationSec ?? 0));

  // Mídia ausente (`media.status` não `ready`, seed antigo): estado único e
  // limpo — sem player, sem cabeçalho, sem legenda duplicada. O `MessageMedia`
  // suprime a legenda placeholder neste caso; aqui sai só ícone + rótulo +
  // duração quando conhecida. Com `url`, o player normal volta.
  if (!url) {
    const missingLabel = formatAudioTime(durationSec ?? 0);
    const hasKnownDuration = durationSec !== null && durationSec !== undefined && durationSec > 0;
    return (
      <div className="audio-unavail" role="status" aria-label="Áudio indisponível">
        <span className="voice-file-ic" aria-hidden="true">
          <Icon name="audio" size={20} />
        </span>
        <span className="audio-unavail-label">Áudio indisponível</span>
        {hasKnownDuration ? <span className="audio-time">{missingLabel}</span> : null}
      </div>
    );
  }

  return (
    <div className={cx("voice-player", kind === "audio" && "is-file")}>
      {kind === "audio" ? (
        <div className="voice-file-head">
          <span className="voice-file-ic" aria-hidden="true">
            <Icon name="audio" size={20} />
          </span>
          <span className="voice-file-name" title={fileName ?? undefined}>
            {fileName?.trim() ? fileName : "Áudio"}
          </span>
        </div>
      ) : null}

      <div className="voice-player-row">
        <button
          type="button"
          className={cx("audio-play", listened ? "is-listened" : "is-fresh")}
          aria-label={playing ? `Pausar ${label}` : `Ouvir ${label}`}
          aria-pressed={playing}
          disabled={!url}
          onClick={toggle}
        >
          <span className="audio-play-ic" aria-hidden="true">
            <Icon name={playing ? "pause" : "play"} size={18} />
          </span>
        </button>

        <div
          className={cx("audio-wave tg-seek", dragging && "is-dragging")}
          role="slider"
          tabIndex={url ? 0 : -1}
          aria-label={`Buscar em ${label}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
          aria-valuetext={`${timeLabel} de ${formatAudioTime(durationSec ?? 0)}`}
          onClick={onWaveClick}
          onKeyDown={onWaveKeyDown}
          onPointerDown={(event) => {
            if (event.button !== 0) return;
            event.currentTarget.setPointerCapture(event.pointerId);
            setDragging(true);
            seekFromEvent(event.currentTarget, event.clientX);
          }}
          onPointerMove={(event) => {
            if (!dragging) return;
            event.preventDefault();
            seekFromEvent(event.currentTarget, event.clientX);
          }}
          onPointerUp={(event) => {
            setDragging(false);
            event.currentTarget.releasePointerCapture(event.pointerId);
          }}
          onPointerCancel={() => setDragging(false)}
        >
          {shownBars.map((level, index) => (
            <i
              key={index}
              aria-hidden="true"
              data-played={index < playedBars}
              // Altura direta, nao `scaleY`: a barra tem 3px e canto redondo,
              // e escalar deformaria o circulo da barra mais baixa.
              style={{ height: `${Math.max(3, Math.round(level * 21))}px` }}
            />
          ))}
          <span className="audio-wave-dot" aria-hidden="true" style={{ left: `${progress * 100}%` }} />
        </div>

        {playing || rate !== 1 ? (
          <button
            type="button"
            className="voice-rate"
            aria-label={`Velocidade de reprodução: ${rateLabel(rate)}. Tocar para mudar.`}
            title={`Velocidade ${rateLabel(rate)}`}
            disabled={!url}
            onClick={toggleRate}
          >
            {rateLabel(rate)}
          </button>
        ) : null}
      </div>

      <div className="voice-player-foot">
        <span className="audio-time" role="timer" aria-label={`Duração ${formatAudioTime(durationSec ?? 0)}`}>
          {timeLabel}
        </span>
        {!url || failed ? (
          <span className="audio-time" role="status">
            Áudio indisponível
          </span>
        ) : null}
      </div>

      {url ? (
        <audio ref={audioRef} src={url} preload="metadata" aria-hidden="true" tabIndex={-1} {...audioProps} />
      ) : null}
    </div>
  );
}
